import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { Gift as GiftCardIcon } from "lucide-react";
import AutobetLogo from "../../../../../static/Images/logo.png";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

const GiftCardDetailVisual = ({
  imageUrl,
  cardId,
  isDonation = false,
  tokenValue = "0",
  tokenName = "BET",
  description = "",
}) => {
  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
      }}
    >
      {/* Artwork Visual Stage */}
      <Box
        sx={{
          height: { xs: 260, sm: 340 },
          width: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          background: isDonation
            ? `radial-gradient(circle at 50% 30%, ${colors.amber[50]}, ${colors.slate[50]})`
            : `radial-gradient(circle at 50% 30%, ${colors.blue[50]}, ${colors.slate[50]})`,
          borderBottom: `1px solid ${colors.slate[200]}`,
        }}
      >
        {/* Card Artwork Image */}
        <Box
          component="img"
          src={imageUrl || AutobetLogo}
          alt={`Gift Card #${cardId}`}
          sx={{
            maxHeight: "100%",
            maxWidth: "85%",
            objectFit: "contain",
            filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.08))",
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "scale(1.03)",
            },
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = AutobetLogo;
          }}
        />

        {/* Floating Value Pill */}
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: borderRadiusPx.full,
            bgcolor: colors.slate[900],
            border: `1px solid ${colors.slate[800]}`,
            boxShadow: "0 6px 16px rgba(15, 23, 42, 0.35)",
            zIndex: 2,
          }}
        >
          <GiftCardIcon size={16} color={colors.amber[400]} />
          <Typography
            component="span"
            sx={{
              color: colors.neutral.white,
              fontWeight: 800,
              fontSize: "0.95rem",
              letterSpacing: "0.02em",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {tokenValue}{" "}
            <Box
              component="span"
              sx={{
                color: colors.amber[400],
                fontWeight: 800,
              }}
            >
              {tokenName}
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Card Info & Description Body */}
      <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Stack spacing={2}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: colors.slate[900],
              fontSize: "1.1rem",
              textAlign: "left",
            }}
          >
            About This Gift Card
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: colors.slate[700],
              fontSize: "0.95rem",
              lineHeight: 1.65,
              textAlign: "left",
              whiteSpace: "pre-line",
            }}
          >
            {description ||
              "This digital gift card represents securely locked ERC-20 tokens deployed on the blockchain. Once claimed or purchased, the smart contract immediately transfers the underlying tokens to the buyer's wallet."}
          </Typography>
        </Stack>
      </Box>
    </Card>
  );
};

GiftCardDetailVisual.propTypes = {
  imageUrl: PropTypes.string,
  cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isDonation: PropTypes.bool,
  tokenValue: PropTypes.string,
  tokenName: PropTypes.string,
  description: PropTypes.string,
};

export default GiftCardDetailVisual;
